import { useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge, type Tone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { daysUntil, formatDateShort, formatDayOf } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { downloadFile, fileName, formatBytes, viewFile } from '@/lib/open-file'
import { describeError } from '@/lib/supabase'
import { useGenerateInvoicePdf, useJobInvoices } from '@/features/invoices/queries'
import { InvoiceStatusBadge, invoiceNumber, invoiceTitle } from '@/features/invoices/shared'
import { useGenerateQuotePdf, useJobQuotes, type Quote } from '@/features/quotes/queries'
import { STATUS_TONE } from '@/features/questionnaires/QuestionnairesPage'
import { CONTRACT_STATUS } from '@/features/contracts/ContractsPage'
import { useJobContracts } from '@/features/contracts/queries'
import { questionnairePdfUrl, useJobQuestionnaires } from '@/features/questionnaires/queries'
import { QUESTIONNAIRE_STATUS_LABEL, questionnaireStatus } from '@/lib/questionnaire'
import { jobFileUrl, useAddJobFile, useJobFiles, useRemoveJobFile, useSetFileShared, type DocumentSlot, type JobFile } from './queries'

/**
 * Everything sent to the couple, one list (28 Oct 2026): the quote, the
 * invoices, the contract, the questionnaire, and files (a timeline, an
 * inspiration board, the family list), each with View and Download.
 *
 * What the couple can see in their portal is marked: drafts never show, and a
 * file Jena adds can be kept to herself.
 */
export function JobDocuments({ jobId, jobTitle, contractSignedAt }: { jobId: string; jobTitle: string; contractSignedAt: string | null }) {
  const quotes = useJobQuotes(jobId)
  const invoices = useJobInvoices(jobId)
  const files = useJobFiles(jobId)
  const questionnaires = useJobQuestionnaires(jobId)
  const contracts = useJobContracts(jobId)
  const quotePdf = useGenerateQuotePdf()
  const invoicePdf = useGenerateInvoicePdf()

  const loading = quotes.isPending || invoices.isPending || files.isPending
  const uploaded = (slot: DocumentSlot) => (files.data ?? []).filter((file) => file.slot === slot)
  const uploadedRows = (slot: DocumentSlot) => uploaded(slot).map((file) => <FileRow key={file.id} jobId={jobId} file={file} />)

  return (
    <div className="flex flex-col gap-5">
      {loading ? <p className="text-[length:var(--text-2xs)] text-taupe">Loading documents…</p> : null}

      <Group
        title="Quote"
        action={
          <>
            <Link to={`/quotes?new=${jobId}`} className="label underline underline-offset-4 hover:text-ink">
              New quote
            </Link>
            <UploadButton jobId={jobId} slot="quote" />
          </>
        }
      >
        {(quotes.data ?? []).length === 0 && uploaded('quote').length === 0 && !loading ? <Empty>No quote yet.</Empty> : null}
        {uploadedRows('quote')}
        {(quotes.data ?? []).map((quote) => {
          const status = quoteStatus(quote)
          const name = `Quote ${quote.quote_number}`
          const url = () => quotePdf.mutateAsync({ quoteId: quote.id, jobId })
          return (
            <DocumentRow
              key={quote.id}
              title={name}
              to={`/quotes/${quote.id}`}
              badge={<Badge tone={status.tone}>{status.label}</Badge>}
              amount={Number(quote.total)}
              facts={[
                quote.sent_at ? `Sent ${formatDayOf(quote.sent_at)}` : 'Not sent yet: not in the portal',
                quote.viewed_at ? `Opened ${formatDayOf(quote.viewed_at)}` : null,
                quote.accepted_at ? `Accepted ${formatDayOf(quote.accepted_at)}` : null,
              ]}
              onView={() => viewFile(url)}
              onDownload={() => downloadFile(url, `${fileName(jobTitle, name)}.pdf`)}
            />
          )
        })}
      </Group>

      <Group
        title="Invoices"
        action={
          <>
            <Link to={`/invoices?new=${jobId}`} className="label underline underline-offset-4 hover:text-ink">
              New invoice
            </Link>
            <UploadButton jobId={jobId} slot="invoice" />
          </>
        }
      >
        {(invoices.data ?? []).length === 0 && uploaded('invoice').length === 0 && !loading ? (
          <Empty>No invoices yet. Accepting a quote raises the invoice.</Empty>
        ) : null}
        {uploadedRows('invoice')}
        {(invoices.data ?? []).map((invoice) => {
          const name = `${invoiceTitle(invoice.kind)} ${invoiceNumber(invoice.invoice_number)}`
          const url = () => invoicePdf.mutateAsync({ invoiceId: invoice.id, jobId })
          return (
            <DocumentRow
              key={invoice.id}
              title={name}
              to={`/invoices/${invoice.id}`}
              badge={<InvoiceStatusBadge invoice={invoice} />}
              amount={Number(invoice.total)}
              struck={invoice.status === 'void'}
              facts={[
                invoice.status === 'draft' ? 'Draft: not in the portal' : invoice.sent_at ? `Sent ${formatDayOf(invoice.sent_at)}` : `Issued ${formatDateShort(invoice.issue_date)}`,
                invoice.viewed_at ? `Opened ${formatDayOf(invoice.viewed_at)}` : null,
                invoice.paid_at ? `Paid ${formatDayOf(invoice.paid_at)}` : `Due ${formatDateShort(invoice.due_date)}`,
              ]}
              onView={() => viewFile(url)}
              onDownload={() => downloadFile(url, `${fileName(jobTitle, name)}.pdf`)}
            />
          )
        })}
      </Group>

      <Group
        title="Contract"
        action={
          <>
            <Link to={`/contracts?new=${jobId}`} className="label underline underline-offset-4 hover:text-ink">
              New contract
            </Link>
            <UploadButton jobId={jobId} slot="contract" />
          </>
        }
      >
        {uploaded('contract').length === 0 && (contracts.data ?? []).length === 0 ? (
          <PlaceholderRow
            title="Wedding contract"
            detail={
              contractSignedAt
                ? `Signed ${formatDayOf(contractSignedAt)}, in Studio Ninja. Upload the signed PDF to keep it here.`
                : 'In Studio Ninja for now: upload the PDF to keep it here, or write one with New contract.'
            }
          />
        ) : null}
        {(contracts.data ?? []).map((contract) => (
          <li key={contract.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-b py-2">
            <p className="flex flex-wrap items-baseline gap-x-2 text-[length:var(--text-sm)]">
              <Link to={`/contracts/${contract.id}`} className="hover:underline">
                {contract.title}
              </Link>
              <Badge tone={CONTRACT_STATUS[contract.status]?.tone ?? 'neutral'}>{CONTRACT_STATUS[contract.status]?.label ?? contract.status}</Badge>
            </p>
          </li>
        ))}
        {uploadedRows('contract')}
      </Group>

      <Group
        title="Questionnaire"
        action={
          <>
            <Link to={`/questionnaires?new=${jobId}`} className="label underline underline-offset-4 hover:text-ink">
              New questionnaire
            </Link>
            <UploadButton jobId={jobId} slot="questionnaire" />
          </>
        }
      >
        {uploaded('questionnaire').length === 0 && (questionnaires.data ?? []).length === 0 && !questionnaires.isPending ? (
          <Empty>No questionnaire yet. Send one, or upload their Studio Ninja answers as a PDF.</Empty>
        ) : null}
        {(questionnaires.data ?? []).map((questionnaire) => {
          const status = questionnaireStatus(questionnaire)
          const url = () => questionnairePdfUrl(questionnaire.id)
          return (
            <DocumentRow
              key={questionnaire.id}
              title={questionnaire.name}
              to={`/questionnaires/${questionnaire.id}`}
              badge={<Badge tone={STATUS_TONE[status]}>{QUESTIONNAIRE_STATUS_LABEL[status]}</Badge>}
              facts={[
                questionnaire.sent_at ? `Sent ${formatDayOf(questionnaire.sent_at)}` : 'Not sent yet',
                questionnaire.completed_at ? `Completed ${formatDayOf(questionnaire.completed_at)}` : null,
              ]}
              onView={() => viewFile(url)}
              onDownload={() => downloadFile(url, `${fileName(jobTitle, questionnaire.name)}.pdf`)}
            />
          )
        })}
        {uploadedRows('questionnaire')}
      </Group>

      <Files jobId={jobId} files={(files.data ?? []).filter((file) => !file.slot)} loading={files.isPending} />
    </div>
  )
}

function quoteStatus(quote: Quote): { label: string; tone: Tone } {
  if (quote.status === 'accepted') return { label: 'Accepted', tone: 'sage' }
  if (quote.status === 'declined') return { label: 'Declined', tone: 'clay' }
  if (quote.status === 'draft') return { label: 'Draft', tone: 'neutral' }
  if (quote.valid_until && daysUntil(quote.valid_until) < 0) return { label: 'Expired', tone: 'clay' }
  if (quote.status === 'viewed') return { label: 'Opened', tone: 'amber' }
  return { label: 'Sent', tone: 'amber' }
}

function Group({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="label">{title}</p>
        {action ? <div className="flex flex-wrap items-center gap-3">{action}</div> : null}
      </div>
      <ul className="flex flex-col">{children}</ul>
    </div>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <li className="border-b py-2 text-[length:var(--text-sm)] text-taupe">{children}</li>
}

function PlaceholderRow({ title, detail }: { title: string; detail: string }) {
  return (
    <li className="border-b py-2">
      <p className="text-[length:var(--text-sm)] text-taupe">{title}</p>
      <p className="text-[length:var(--text-2xs)] text-taupe">{detail}</p>
    </li>
  )
}

/** One document: its name, what happened to it and when, and View / Download. */
function DocumentRow({
  title,
  to,
  badge,
  amount,
  struck = false,
  facts,
  onView,
  onDownload,
  extra,
}: {
  title: string
  to?: string
  badge?: ReactNode
  amount?: number
  struck?: boolean
  facts: Array<string | null>
  onView: () => Promise<void>
  onDownload: () => Promise<void>
  extra?: ReactNode
}) {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null)

  async function run(which: 'view' | 'download', action: () => Promise<void>) {
    setBusy(which)
    try {
      await action()
    } catch (error) {
      toast.error(which === 'view' ? 'Could not open it' : 'Could not download it', { description: describeError(error) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-b py-2">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 text-[length:var(--text-sm)]">
          {to ? (
            <Link to={to} className={struck ? 'text-taupe line-through hover:underline' : 'hover:underline'}>
              {title}
            </Link>
          ) : (
            <span className="break-all">{title}</span>
          )}
          {badge}
          {amount !== undefined ? (
            <span className={struck ? 'text-taupe line-through' : 'text-taupe'} data-numeric>
              {formatMoney(amount)}
            </span>
          ) : null}
        </p>
        <p className="text-[length:var(--text-2xs)] text-taupe">{facts.filter(Boolean).join(' · ')}</p>
        {extra}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="sm" variant="ghost" loading={busy === 'view'} loadingLabel="Opening" onClick={() => void run('view', onView)}>
          View
        </Button>
        <Button size="sm" variant="ghost" loading={busy === 'download'} loadingLabel="Preparing" onClick={() => void run('download', onDownload)}>
          Download
        </Button>
      </div>
    </li>
  )
}

const SLOT_NOUN: Record<DocumentSlot, string> = { quote: 'a quote', invoice: 'an invoice', contract: 'a contract', questionnaire: 'a questionnaire' }

/**
 * Upload a PDF into a document's own place (28 Oct 2026): a quote, invoice,
 * contract or questionnaire made in Studio Ninja or elsewhere sits where the
 * Studio's own would, instead of under Files. Without a slot it is a file.
 */
function UploadButton({ jobId, slot = null }: { jobId: string; slot?: DocumentSlot | null }) {
  const add = useAddJobFile(jobId)
  const input = useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        ref={input}
        type="file"
        multiple={!slot}
        accept={slot ? 'application/pdf,image/*' : undefined}
        className="hidden"
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? [])
          event.target.value = ''
          for (const file of chosen) add.mutate({ file, shared: true, slot }, { onSuccess: () => toast.success(`${file.name} added`) })
        }}
      />
      {slot ? (
        <button type="button" className="label underline underline-offset-4 hover:text-ink disabled:opacity-60" disabled={add.isPending} onClick={() => input.current?.click()}>
          {add.isPending ? 'Uploading…' : `Upload ${SLOT_NOUN[slot]}`}
        </button>
      ) : (
        <Button size="sm" variant="secondary" loading={add.isPending} loadingLabel="Uploading" onClick={() => input.current?.click()}>
          Add a file
        </Button>
      )}
    </>
  )
}

function FileRow({ jobId, file }: { jobId: string; file: JobFile }) {
  const setShared = useSetFileShared(jobId)
  const remove = useRemoveJobFile(jobId)
  const url = () => jobFileUrl(file.file_path)

  return (
    <DocumentRow
      title={file.name}
      badge={file.added_by === 'couple' ? <Badge tone="amber">From the couple</Badge> : file.slot ? <Badge tone="neutral">Uploaded</Badge> : null}
      facts={[`Added ${formatDayOf(file.uploaded_at ?? file.created_at)}`, formatBytes(file.size_bytes) || null]}
      onView={() => viewFile(url)}
      onDownload={() => downloadFile(url, file.name)}
      extra={
        <div className="mt-1 flex flex-wrap items-center gap-x-4">
          {file.added_by === 'studio' ? (
            <label className="flex cursor-pointer items-center gap-1.5 text-[length:var(--text-2xs)] text-taupe">
              <input
                type="checkbox"
                className="check size-[13px]"
                checked={file.shared}
                onChange={(event) => setShared.mutate({ id: file.id, shared: event.target.checked })}
              />
              Show in their portal
            </label>
          ) : null}
          <button
            type="button"
            className="text-[length:var(--text-2xs)] text-taupe hover:text-clay"
            onClick={() => {
              if (window.confirm(`Remove "${file.name}"? It is deleted for good.`)) remove.mutate(file)
            }}
          >
            Remove
          </button>
        </div>
      }
    />
  )
}

function Files({ jobId, files, loading }: { jobId: string; files: JobFile[]; loading: boolean }) {
  return (
    <Group title="Files" action={<UploadButton jobId={jobId} />}>
      {files.length === 0 && !loading ? <Empty>The timeline, inspiration, the family list: add them here. Files the couple uploads in their portal land here too.</Empty> : null}
      {files.map((file) => (
        <FileRow key={file.id} jobId={jobId} file={file} />
      ))}
    </Group>
  )
}
